import PlaceModel                  from '@models/map/PlaceModel';
import type { Action }             from '@widesk-ui/components/ButtonActions';
import Form                        from '@widesk-ui/components/form/Form';
import Input                       from '@widesk-ui/components/form/Input';
import SelectPlaceType             from '@selects/map/SelectPlaceType';
import React                       from 'react';
import View                        from '@widesk-ui/components/View';
import PlaceTypeModel              from '@models/map/PlaceTypeModel';
import Observer                    from '@widesk-ui/components/Observer';
import useApiCollection            from '@widesk-core/hooks/useApiCollection';
import PlaceTypeFieldModel         from '@models/map/PlaceTypeFieldModel';
import { getIdFromUrn }            from '@widesk-core/tools/identifier';
import { FormItem }                from '@widesk-ui/components/form/FormItem';
import PlaceFieldModel             from '@models/map/PlaceFieldModel';
import toTranslationsPayload       from '@/helpers/toTranslationsPayload';
import Alert                       from '@widesk-ui/components/Alert';
import ApiCollection               from '@widesk-core/models/ApiCollection';
import ColorPicker                 from '@widesk-ui/components/form/ColorPicker';
import MultiLangHorizontalTabs     from '@widesk-ui/components/MultiLangHorizontalTabs';
import languages                   from '@/languages';
import TextArea                    from '@widesk-ui/components/form/TextArea';
import InputNumber                 from '@widesk-ui/components/form/InputNumber';
import { FormTools }               from '@widesk-ui/components/form/Form';
import FileInput                   from '@widesk-ui/components/form/FileInput';
import Divider                     from '@widesk-ui/components/Divider';
import fileStore                   from '@/stores/fileStore';
import updateSingleFileForModelUrn from '@/helpers/updateSingleFileForModelUrn';
import BatchManager                from '@widesk-core/models/batch/BatchManager';
import useDesignTokens              from '@/hooks/useDesignTokens';
import { PlusOutlined }             from '@ant-design/icons';
import { RightOutlined }            from '@ant-design/icons';
import { DownOutlined }             from '@ant-design/icons';
import { CheckOutlined }            from '@ant-design/icons';
import Popover                      from '@widesk-ui/components/Popover';
import { DeleteOutlined }           from '@ant-design/icons';
import { EditOutlined }             from '@ant-design/icons';
import UrlFieldInput                from '@/TokamapEditor/ux/UrlFieldInput';
import Field                        from '@/TokamapEditor/ux/AreaSidebar/Field';
import PlacePanelPreview            from '@/TokamapEditor/ux/PlacePanelPreview';
import buildPlacePanelEntry         from '@/TokamapEditor/helpers/placePanelEntry';
import FieldModel                   from '@models/map/FieldModel';
import deleteShapesFromPlace        from '@/TokamapEditor/helpers/deleteShapesFromPlace';
import OneLineSkeleton             from '@widesk-ui/components/OneLineSkeleton';
import Spin                        from '@widesk-ui/components/Spin';
import InputLock                   from '@widesk-ui/components/form/InputLock';
import debounce                    from 'lodash/debounce';
import { message }                 from '@widesk-ui/hooks/useMessage';
import leftSidebarStore            from '@/TokamapEditor/stores/leftSidebarStore';
import refreshShapesOfPlace        from '@/TokamapEditor/helpers/refreshShapesOfPlace';
import SaveIndicator               from '@/TokamapEditor/ux/SaveIndicator';
import EditableImage               from '@/components/EditableImage';
import imageOf                     from '@/components/imageOf';
import ImageUrlInput               from '@/TokamapEditor/ux/ImageUrlInput';
import PlaceTypeForm                from '@/forms/services/map/PlaceTypeForm';
import nextFreeLabel                from '@/tools/nextFreeLabel';
import { pickFreeColor }            from '@/TokamapEditor/helpers/randomColor';
import messageWithUndo              from '@/TokamapEditor/helpers/messageWithUndo';
import { SaveState }               from '@/TokamapEditor/ux/SaveIndicator';
import EditableText                 from '@widesk-ui/components/form/EditableText';
import { observer }                from 'mobx-react-lite';
import { functionalityStore }      from '@/components/Functionality';

export type PlaceFormProps = {
	model: PlaceModel;
	onSuccess?: () => void;
	projectUrn: Urn;
	/** Modification dans la sidebar : plus de bouton, chaque changement part de lui-même. */
	inSidebar?: boolean;
	/** Actions ajoutées avant la suppression dans le menu de l'en-tête (voir `PlaceShapePanel`). */
	headerActions?: Action[];
	/** Taille de la vignette de l'en-tête, relue à chaque rendu de celui-ci (voir `PlaceShapePanel`). */
	headerImageSize?: () => number | undefined;
}

const PlaceTypeFieldInputs = (props: {
	placeTypeFieldCollection: ApiCollection<PlaceTypeFieldModel>;
	/** Nommé dans l'invitation à ajouter un champ : dire « à "Boutique" » rappelle au passage que
	 * c'est le TYPE qui porte les champs, pas ce point d'intérêt-ci. */
	placeType: PlaceTypeModel;
	/** Ouvre la fiche du type. L'état vide serait sinon un constat sans suite : c'est de là que les
	 * champs s'ajoutent, et on sait l'ouvrir par-dessus (niveau 3). */
	onConfigureType?: () => void;
}) => {
	const tokens = useDesignTokens();

	return (
		<Observer render={() => {
			// Une ébauche à la bonne forme plutôt qu'un `Spin` : celui-ci tient sur une trentaine de
			// pixels, que les champs remplacent ensuite par trois cents — tout ce qui suit dans la fiche
			// (options avancées, aperçu) faisait un bond au moment où ils arrivaient. Le nombre de champs
			// n'est connu qu'une fois la requête finie ; trois lignes est la taille courante d'un type, et
			// l'écart résiduel se joue sur une ligne ou deux au lieu de la section entière.
			if (props.placeTypeFieldCollection.isLoading) {
				return (
					<View gap="sm">
						{[0, 1, 2].map(i => (
							<View key={i} gap={4}>
								<OneLineSkeleton width={70 + i * 24} />

								<View h={32} br={6} bg={tokens.fill.tertiary} />
							</View>
						))}
					</View>
				);
			}

			// `label` lu inconditionnellement, et AVANT tout test : c'est cette lecture qui déclenche la
			// résolution de la relation. Le tester par `isLoaded` d'abord, comme je le faisais, ne
			// chargeait rien — le libellé n'apparaissait donc que si un autre écran avait déjà résolu ce
			// type, d'où un suffixe « à "Type 1" » présent une fois sur deux.
			const typeLabel = props.placeType.label;

			// Rien à titrer : quand le type ne demande aucun champ, l'invitation remplace la section —
			// séparateur compris. Annoncer "Champs du type" au-dessus d'un vide revenait à dire deux fois
			// qu'il n'y a rien. Bordure pointillée comme la tuile "Nouveau type" de la création : c'est le
			// même geste, créer ce qui manque là où il manque.
			if (!props.placeTypeFieldCollection.length) {
				return (
					<View
						center
						gap={6}
						py="lg"
						px="md"
						br={8}
						pointer
						color={tokens.color.primary}
						hover={{ bg: tokens.surface.subtle }}
						style={{ border: `1px dashed ${tokens.border.default}` }}
						onClick={props.onConfigureType}
					>
						<View row centerV gap="xs" bold size="small">
							<PlusOutlined />
							Ajouter {FieldModel.localize('a', 'l')}
							{typeLabel ? ` à « ${typeLabel} »` : ''}
						</View>

						{/* Dit ce qu'est un champ, et surtout que l'ajout ne concerne pas ce point d'intérêt
						    seul : c'est ce qui explique que le bouton mène à la fiche du TYPE, ce qui sinon
						    ressemble à une erreur de destination. */}
						<View size="small" center lineHeight={1.45} color={tokens.text.muted} style={{ maxWidth: 280 }}>
							Les {FieldModel.localize('plural', 'l')} sont les informations à renseigner ici —
							horaires, téléphone, photo… Ils se définissent sur le type et valent pour tous
							les {PlaceModel.localize('plural', 'l')} qui le portent.
						</View>
					</View>
				);
			}

			// `gap="sm"` et non le `lg` du conteneur : ces champs étaient rendus à plat parmi les sections
			// de la fiche, ils héritaient donc de l'écart PRÉVU ENTRE SECTIONS — 24px entre chaque ligne.
			//
			// `Field` plutôt que l'étiquette d'antd (`FormItem label`), qui réserve son propre espace sous
			// le libellé : c'est le composant d'étiquette de l'éditeur, celui de la sidebar d'une forme,
			// et il ne coûte que 4px.
			return (
				<View gap="sm">
					{props.placeTypeFieldCollection.map(placeTypeField => {
						const field = placeTypeField.field;
						const typeReference = field.fieldType.reference;

						// Pas de valeur initiale (`https://`, `#000000` auparavant) : la fiche enregistre au
						// fil de l'eau, et une valeur posée d'office aurait été écrite sur tous les champs
						// jamais remplis dès la première frappe ailleurs dans l'écran. Le `placeholder`, lui,
						// ne laisse rien derrière — il donne juste un repère dans un champ vide.
						if (typeReference === 'translatable') {
							return (
								<Field key={placeTypeField.id} label={field.label}>
									<MultiLangHorizontalTabs items={languages.map(lang => ({
										formItems: () => [
											<FormItem
												key={`${placeTypeField.id}-${lang}`}
												name={[`placeTypeField.${placeTypeField.id}`, lang, 'value']}
											>
												<TextArea placeholder={field.label} autoSize={{ minRows: 1, maxRows: 8 }} />
											</FormItem>,
										],
										lang,
									}))} />
								</Field>
							);
						}

						let FieldTypeInput;
						switch (typeReference) {
							case 'number':
								// Sans `addonBefore="Nombre"` : l'encart nommait la NATURE du champ, pas son
								// contenu, et prenait un tiers de la largeur d'une sidebar de 400px pour le faire.
								FieldTypeInput = <InputNumber placeholder={field.label} />;
								break;

							case 'url':
								FieldTypeInput = <UrlFieldInput placeholder={field.label} />;
								break;

							case 'color':
								// `allowClear` : sans lui, une couleur posée par erreur ne pouvait plus être
								// retirée. `rightTop` parce qu'une sidebar est étroite et haute — le repli
								// haut/bas d'antd n'y suffit pas (voir `ColorPickerProps.placement`).
								FieldTypeInput = <ColorPicker allowClear showText placement="rightTop" />;
								break;

							case 'image':
								FieldTypeInput = <ImageUrlInput />;
								break;

							case 'string':
								// Hauteur suivant le contenu : "9h – 19h" tient sur une ligne, une description en
								// prend autant qu'il faut. Le `TextArea` à hauteur fixe donnait trois lignes vides
								// à tous.
								FieldTypeInput = <TextArea placeholder={field.label} autoSize={{ minRows: 1, maxRows: 8 }} />;
								break;

							default:
								FieldTypeInput = <Input placeholder={field.label} />;
						}

						return (
							<Field key={placeTypeField.id} label={field.label}>
								<FormItem name={`placeTypeField.${placeTypeField.id}`}>
									{FieldTypeInput}
								</FormItem>
							</Field>
						);
					})}
				</View>
			);
		}} />
	);
};

/**
 * Nom modifiable en place dans l'en-tête de la sidebar, comme pour un type (`PlaceTypeTitle`) ou un
 * thème. La fiche a aussi son champ « Nom » : `runSave` le resynchronise après un renommage d'ici,
 * et l'en-tête relit `model.label` après un renommage depuis la fiche.
 */
export const PlaceTitle = observer((props: {
	model: PlaceModel;
	/** Enveloppe d'enregistrement du panneau : passée par `PlaceForm`, elle fait apparaître le même
	 * "Enregistré" que les autres champs. Absente quand la liste pose le titre avant que le
	 * formulaire ne soit monté — le renommage marche alors, simplement sans indicateur. */
	onSave?: (task: () => Promise<void>) => Promise<void>;
}) => (
	<EditableText
		value={props.model.label}
		placeholder="Titre du point d'intérêt"
		errorMessage="Impossible de renommer ce point d'intérêt"
		onSubmit={async label => {
			// `EditableText` affiche le message de l'`Error` levée, sous le champ resté ouvert.
			if (!label) throw new Error('Le titre est obligatoire.');

			const task = async () => {
				await props.model.patch({ label });

				leftSidebarStore.getListStore('places')?.reload();
				void refreshShapesOfPlace(props.model);
			};

			await (props.onSave ? props.onSave(task) : task());
		}}
	/>
));

/** Référence du type de fichier de l'image d'un point d'intérêt, côté service de fichiers. */
const PLACE_IMAGE = 'place_image';

/**
 * Image du point d'intérêt, dans l'en-tête de la sidebar — au même endroit que la pastille d'une
 * forme (`ShapeSwatch`) ou d'un réglage (`SettingsSwatch`).
 *
 * Elle y est à sa place pour la même raison que le titre : c'est ce qui identifie l'objet, pas une
 * donnée parmi les autres. Elle sort du coup du formulaire — l'en-tête est rendu par la sidebar, hors
 * de l'arbre React du `Form` — et gère donc son propre enregistrement.
 */
export const PlaceHeaderImage = (props: {
	model: PlaceModel;
	size?: number;
	/** Même enveloppe que `PlaceTitle` : l'indicateur "Enregistré" de l'en-tête, quand le formulaire
	 * est monté pour la fournir. */
	onSave?: (task: () => Promise<void>) => Promise<void>;
}) => (
	<EditableImage
		model={props.model}
		size={props.size}
		reference={PLACE_IMAGE}
		onSave={props.onSave}
		onChanged={() => {
			leftSidebarStore.getListStore('places')?.reload();
			void refreshShapesOfPlace(props.model);
		}}
	/>
);

export default function PlaceForm({
	model,
	projectUrn,
	onSuccess,
	inSidebar,
	headerActions = [],
	headerImageSize,
}: PlaceFormProps) {
	const formToolsRef = React.useRef<FormTools<any>>();

	/**
	 * `Form` appelle son `onChange` pour tout changement de valeur, sans distinguer la frappe de
	 * l'utilisateur d'un remplissage fait par le code — et il l'appelle en plus UNE FOIS AU MONTAGE,
	 * juste après `onDidMount` (voir `Form.tsx`). Ouvrir une fiche déclenchait donc un enregistrement
	 * complet, sans que rien n'ait été modifié.
	 *
	 * Deux verrous, parce que les deux cas sont distincts :
	 *  - `armed` : tant qu'il est faux, le formulaire n'est pas encore passé sous la main de
	 *    l'utilisateur. Il couvre l'appel du montage.
	 *  - `filling` : levé le temps d'un remplissage par le code — chargement des valeurs, arrivée des
	 *    champs d'un type, resynchronisation après un renommage depuis l'en-tête.
	 */
	const armedRef = React.useRef(false);
	const fillingRef = React.useRef(0);

	/**
	 * Incrémenté à chaque remplissage par le code, pour redessiner ce qui LIT les valeurs du
	 * formulaire sans passer par un champ — l'aperçu, en bas de la fiche.
	 *
	 * Les valeurs vivent dans le store d'antd, qui n'est pas observable : les poser ne provoque aucun
	 * rendu. L'aperçu restait donc vide à l'ouverture, et ne se peuplait qu'à la première frappe,
	 * celle-ci déclenchant le rendu complet d'`updateOnChange`.
	 */
	const [filledCount, setFilledCount] = React.useState(0);

	const fill = (values: Record<string, unknown>) => {
		fillingRef.current += 1;
		formToolsRef.current?.setFieldsValue(values);
		setFilledCount(count => count + 1);

		// Relâché au tour de boucle suivant : antd notifie ses changements de façon synchrone, mais
		// `updateOnChange` provoque un rendu qui peut en décaler une partie.
		setTimeout(() => {
			fillingRef.current = Math.max(0, fillingRef.current - 1);
		});
	};
	const [placeTypeUrn, setPlaceTypeUrn] = React.useState<Urn>();
	const tokens = useDesignTokens();

	const created = !!model.id;

	/**
	 * Faux tant que la fiche n'a pas TOUT ce qu'elle affiche : ses valeurs, puis les champs de son
	 * type — deux chargements en chaîne, le second ne pouvant partir qu'une fois le type connu.
	 *
	 * Sans ça la fiche se montait par morceaux et se réagençait à chaque arrivée. Un seul temps
	 * d'attente, puis l'écran complet, se lit mieux qu'un écran qui se construit sous les yeux.
	 *
	 * Posé une fois pour toutes — changer de type ensuite recharge les champs, mais c'est
	 * alors un geste de l'utilisateur, avec son propre chargement au bon endroit.
	 */
	const [ready, setReady] = React.useState(!model.id);

	// Profondeur figée au montage, comme dans `PlaceTypeForm` : l'en-tête à décorer est celui de CE
	// panneau, pas du dernier empilé — la fiche d'un type peut s'ouvrir par-dessus.
	const depth = React.useRef(leftSidebarStore.panels.length - 1).current;

	// Tout ce qui concerne le type passe par un menu posé sur sa tuile : en changer, ouvrir sa fiche,
	// en créer un. Un menu se referme tout seul en cliquant ailleurs — il n'ouvre pas une section dans
	// la page, et n'a donc pas besoin d'un bouton "Annuler" pour en sortir.
	const [typeMenuOpen, setTypeMenuOpen] = React.useState(false);

	// Replié par défaut : la référence est un identifiant verrouillé et l'emplacement une note de
	// terrain — ni l'un ni l'autre n'est ce qu'on vient faire ici, et les laisser en haut de l'écran
	// repoussait le contenu réel du point d'intérêt sous la ligne de flottaison.
	const [advancedOpen, setAdvancedOpen] = React.useState(false);

	// Types du projet, pour le menu du type. Une requête, et seulement quand il est ouvert.
	const placeTypeCollection = useApiCollection(PlaceTypeModel);

	React.useEffect(() => {
		if (created && !typeMenuOpen) return;

		placeTypeCollection.setRequiredFilter('project', getIdFromUrn(projectUrn)).list();
	}, [created, typeMenuOpen, projectUrn]);

	const placeFieldCollection = useApiCollection(PlaceFieldModel);
	const placeTypeFieldCollection = useApiCollection(PlaceTypeFieldModel);

	const placeImage = created ? imageOf(model, PLACE_IMAGE) : undefined;

	// `save` est capturé une fois pour toutes par `autoSave` (mémoïsé sur `model`), qui vit désormais
	// au-delà de la création : sans ce relais, il continuerait de voir l'image d'avant — c'est-à-dire
	// aucune — et un remplacement laisserait l'ancien fichier derrière lui.
	const placeImageRef = React.useRef(placeImage);
	placeImageRef.current = placeImage;

	const fetchPlaceFields = async () => {
		const collTranslatablePlaceField = new ApiCollection(PlaceFieldModel);
		await collTranslatablePlaceField
			.setLocale('*')
			.setFilter('field.fieldType.reference', 'translatable')
			.listBy(model.id, 'place');

		const collOtherPlaceField = new ApiCollection(PlaceFieldModel);
		await collOtherPlaceField
			.setFilter('field.fieldType.reference', ['url', 'string', 'color', 'number', 'image'])
			.listBy(model.id, 'place');

		placeFieldCollection.clear();
		[...collTranslatablePlaceField.models, ...collOtherPlaceField.models].map(placeField => {
			placeFieldCollection.add(placeField);
		});

		placeFieldCollection.sortBy('position', 'asc');

		const existingPlaceTypeUrn = formToolsRef.current?.getFieldValue('placeTypeUrn');
		fill({
			label: model.label,
			reference: model.reference,
			location: model.location,
			placeTypeUrn: existingPlaceTypeUrn || model.getUrn('placeType'),
		});

		if (model.getUrn('placeType')) setPlaceTypeUrn(model.getUrn('placeType'));
		// Sans type, personne ne viendra charger des champs : la fiche est prête ici.
		else setReady(true);
	};

	/**
	 * `fetchTypeField` est appelé depuis des rappels posés à l'avance — la fermeture d'un panneau
	 * empilé, par exemple. Lus dans la fermeture lexicale, `placeTypeUrn` y serait celui d'avant : on
	 * relirait alors les champs du type qu'on vient justement de quitter.
	 */
	const placeTypeUrnRef = React.useRef(placeTypeUrn);
	placeTypeUrnRef.current = placeTypeUrn;

	const fetchTypeField = async () => {
		const currentTypeUrn = placeTypeUrnRef.current;

		if (!currentTypeUrn) {
			return;
		}

		await placeTypeFieldCollection
			.sortBy('position', 'asc')
			.listBy([getIdFromUrn(currentTypeUrn)], 'placeType');
		await placeTypeFieldCollection.whenIsLoaded(ptf => ptf.field.fieldType);

		fill((() => {
			const values: any = {};

			placeTypeFieldCollection.forEach(placeTypeField => {
				const placeField = placeFieldCollection.find(pf => {
					return pf.getId('field') === placeTypeField.getId('field');
				});

				const valueKey = `placeTypeField.${placeTypeField.id}`;
				const existingValue = formToolsRef.current?.getFieldValue(valueKey);

				values[valueKey] = placeTypeField?.field.fieldType.reference === 'translatable'
					? existingValue || placeField?.translations
					: existingValue || placeField?.value;
			});

			return values;
		})());

		setReady(true);
	};

	React.useEffect(() => {
		if (placeTypeUrn && formToolsRef.current) {
			fill({ placeTypeUrn });

			(async () => {
				await fetchTypeField();
			})();
		}
	}, [placeTypeUrn, formToolsRef.current]);

	// Vrai seulement en MODIFICATION dans la sidebar : tant que le point d'intérêt n'existe pas, il
	// n'y a rien à enregistrer au fil de l'eau.
	const liveSave = !!inSidebar && created;

	/**
	 * Création d'un type depuis ce formulaire : un panneau de plus sur la pile.
	 *
	 * Le type est créé AVANT d'ouvrir sa fiche — nom généré, couleur libre de la palette — parce que
	 * la fiche d'un type est un écran de modification qui enregistre au fil de l'eau : il n'y a rien
	 * à y enregistrer tant que l'objet n'existe pas. Même geste que depuis la liste des types.
	 *
	 * Il est sélectionné dès sa création, donc un simple retour suffit : l'utilisateur n'a rien à
	 * valider.
	 */
	/**
	 * Retour d'un passage dans la fiche d'un type. Deux choses ont pu y changer, et les deux se voient
	 * ici : les informations que le type demande — c'est le corps de la fiche — et la liste des types
	 * du projet, si l'on vient d'en créer un — c'est le menu du type.
	 *
	 * Le filtre est reposé à chaque fois : la collection des types n'est chargée que lorsque le menu
	 * est ouvert, et ce retour peut arriver avant.
	 */
	const afterTypeSheet = () => {
		fetchTypeField();
		placeTypeCollection.setRequiredFilter('project', getIdFromUrn(projectUrn)).list();
	};

	/**
	 * Changement de type depuis la fiche. Les valeurs déjà saisies pour l'ancien type ne sont pas
	 * effacées — elles restent attachées à leurs champs, et réapparaîtraient si l'on revenait en
	 * arrière ; c'est ce que faisait déjà le sélecteur qu'on remplace.
	 */
	const changeType = async (placeType: PlaceTypeModel) => {
		setTypeMenuOpen(false);
		fill({ placeTypeUrn: placeType.urn });
		setPlaceTypeUrn(placeType.urn);

		try {
			await runSave(async () => {
				await model.patch({ placeType: placeType.urn });

				leftSidebarStore.getListStore('places')?.reload();
			});
		} catch (err) {
			message.exception(err);
		}
	};

	/**
	 * Ouvre la fiche du type par-dessus celle du point d'intérêt. C'est de là que se règlent la
	 * couleur et la liste des informations à renseigner — au retour, on relit les champs, qui ont pu
	 * changer pendant ce passage.
	 */
	const openTypeSheet = () => {
		setTypeMenuOpen(false);

		const placeType = model.placeType;

		if (!placeType?.id) return;

		leftSidebarStore.push({
			title: placeType.label,
			width: 380,
			closeButton: true,
			onClose: afterTypeSheet,
			render: () => <PlaceTypeForm inSidebar model={placeType} projectUrn={projectUrn} />,
		});
	};

	const createAndEditPlaceType = async () => {
		try {
			const existing = new ApiCollection(PlaceTypeModel);
			await existing.setRequiredFilter('project', getIdFromUrn(projectUrn)).list();

			const placeType = new PlaceTypeModel();

			await placeType.patch({
				label: nextFreeLabel('Nouveau type', existing.map(m => m.label).filter(Boolean)),
				color: pickFreeColor(existing.map(m => m.color).filter(Boolean)),
				project: projectUrn,
			});

			setTypeMenuOpen(false);

			if (created) {
				// En modification, créer un type depuis le sélecteur vaut choix : `changeType` l'enregistre
				// sur le point d'intérêt. Sans ça, l'en-tête de la fiche continuait d'annoncer l'ancien
				// type pendant qu'on remplissait les champs du nouveau.
				await changeType(placeType);
			} else {
				fill({ placeTypeUrn: placeType.urn });
				setPlaceTypeUrn(placeType.urn);
			}

			// Titre posé dès l'empilement : `PlaceTypeForm` le remplace au montage par son titre
			// modifiable, mais sans ça le panneau s'ouvrirait sans en-tête le temps d'un rendu.
			leftSidebarStore.push({
				title: placeType.label,
				width: 380,
				closeButton: true,
				onClose: afterTypeSheet,
				render: () => <PlaceTypeForm inSidebar model={placeType} projectUrn={projectUrn} />,
			});

			messageWithUndo(<span>« {placeType.label} » a été créé.</span>, async () => {
				await placeType.destroy();

				// La sélection pointait le type supprimé : on la vide plutôt que de la laisser mentir.
				fill({ placeTypeUrn: undefined });
				setPlaceTypeUrn(undefined);
				leftSidebarStore.pop();
			});
		} catch (err) {
			message.exception(err);
		}
	};

	const [saveState, setSaveState] = React.useState<SaveState>('idle');
	const savedTimeout = React.useRef<ReturnType<typeof setTimeout>>();

	React.useEffect(() => () => clearTimeout(savedTimeout.current), []);

	React.useEffect(() => {
		if (!liveSave) return;

		leftSidebarStore.setPanelOption(depth, 'renderTopRight', () => <SaveIndicator state={saveState} />);
	}, [saveState, liveSave]);

	/**
	 * Enveloppe des enregistrements qui ne passent pas par le formulaire — le renommage depuis
	 * l'en-tête, pour l'instant : même indicateur, et l'erreur remonte à l'appelant, qui sait mieux
	 * comment l'afficher (`EditableText` la montre sous son champ).
	 */
	const runSave = async (task: () => Promise<void>) => {
		clearTimeout(savedTimeout.current);
		setSaveState('saving');

		try {
			await task();

			// Le champ "Titre" est démonté une fois la fiche ouverte, mais antd en conserve la valeur :
			// sans cette resynchronisation, le prochain enregistrement au fil de l'eau réécrirait le
			// titre d'avant le renommage.
			fill({ label: model.label });

			setSaveState('saved');
			savedTimeout.current = setTimeout(() => setSaveState('idle'), 2500);
		} catch (err) {
			setSaveState('error');
			throw err;
		}
	};

	React.useEffect(() => {
		if (!liveSave) return;

		leftSidebarStore.setPanelOption(depth, 'title', <PlaceTitle model={model} onSave={runSave} />);
		leftSidebarStore.setPanelOption(depth, 'renderTopLeft', () => (
			<PlaceHeaderImage model={model} onSave={runSave} size={headerImageSize?.()} />
		));

		// Suppression offerte là où l'on regarde le point d'intérêt, et pas seulement depuis la liste —
		// comme sur la fiche d'un type. Le décompte des zones est relu au moment du clic : la carte a pu
		// changer depuis l'ouverture de la fiche.
		leftSidebarStore.setPanelOption(depth, 'actions', [
			...headerActions,
			{
				icon: <DeleteOutlined />,
				label: `Supprimer ${PlaceModel.localize('the', 'l')}`,
				danger: true,
				confirm: `Voulez-vous supprimer "${model.label}" ? Les zones associées sur la carte seront supprimées avec lui. Cette action est irréversible.`,
				onClick: async () => {
					try {
						await deleteShapesFromPlace(model);
						await model.destroy();

						leftSidebarStore.getListStore('places')?.reload();
						functionalityStore.refreshUsagesOf('place_limit');
						leftSidebarStore.pop();
					} catch (err) {
						message.exception(err);
					}
				},
			},
		]);
	}, [liveSave]);

	/**
	 * Enregistrement complet du point d'intérêt : ses attributs, la valeur de chacun des champs de son
	 * type, et son image. Extrait du `onSubmit` pour être aussi appelable au fil de l'eau, en
	 * modification (voir `autoSave`).
	 *
	 * Volontairement une synchronisation intégrale plutôt qu'un envoi du seul champ modifié : c'est ce
	 * qui la rend rejouable sans état à suivre.
	 *
	 * Le point d'intérêt et ses valeurs de champs partent en UN lot transactionnel : ils ne veulent
	 * rien dire l'un sans l'autre, et un échec au milieu laissait jusqu'ici une fiche à moitié
	 * enregistrée pendant que l'indicateur affichait « erreur ». En création, le lot supprime au
	 * passage un aller-retour : les `placeField` référencent le point d'intérêt que la même requête
	 * vient de créer (`place.urn`), là où il fallait attendre son id.
	 */
	const save = async (values: any) => {
		const batch = new BatchManager();

		// `??` partout : dans la sidebar, plusieurs de ces champs ne sont pas montés — le titre est
		// passé dans l'en-tête, la référence et l'emplacement dorment sous un repli, le type n'est
		// plus un champ du tout. Un champ jamais monté n'a pas de valeur dans le formulaire, et
		// sans ces reprises l'enregistrement au fil de l'eau effacerait ce qu'il ne voit pas.
		// `??` et non `||` : une chaîne vide est une valeur, pas une absence.
		const place = batch.patch(model, {
			// Un nom vidé n'est pas enregistré : le champ signale l'erreur, l'ancien nom reste.
			label: (typeof values.label === 'string' && !values.label.trim()) ? model.label : (values.label ?? model.label),
			reference: values.reference ?? model.reference,
			location: values.location ?? model.location,
			placeType: values.placeTypeUrn ?? model.getUrn('placeType'),
			project: projectUrn,
		});

		placeTypeFieldCollection.forEach(placeTypeField => {
			const placeField = placeFieldCollection.find(
				pf => pf.getId('field') === placeTypeField.getId('field'),
			) || new PlaceFieldModel();

			const value = (values as any)[`placeTypeField.${placeTypeField.id}`] || null;
			if (value === null) {
				if (placeField.id) {
					batch.destroy(placeField);
				}

				return;
			}

			const valueAttribute = placeTypeField.field.fieldType.reference === 'translatable'
				? { translations: toTranslationsPayload(value) }
				: { value: value.toString() };

			batch.patch(placeField, {
				// `place.urn`, pas `model.urn` : à la création, l'urn n'existe pas encore côté front —
				// c'est un jeton que l'API remplace par celle du point d'intérêt créé juste avant.
				place: place.urn,
				field: placeTypeField.getUrn('field'),
				...valueAttribute,
			});
		});

		await batch.run();

		// L'image reste hors du lot : elle passe par le service `file`, que la transaction ne couvre
		// pas, et à la création elle a de toute façon besoin de l'urn que le lot vient de créer.
		// `placeImage` n'est par ailleurs un champ de formulaire qu'en modale : dans la sidebar,
		// l'image se change depuis l'en-tête, et la laisser passer ici la ferait supprimer à chaque
		// enregistrement au fil de l'eau, `updateSingleFileForModelUrn` lisant une valeur absente
		// comme un retrait.
		if (!inSidebar) {
			const newPlaceImage = values.placeImage as FileInputValue | undefined;

			await updateSingleFileForModelUrn(model.urn, PLACE_IMAGE, newPlaceImage?.file, placeImageRef.current);
		}

		await fileStore.fetchForModel(model, { force: true });
		return model;
	};

	/**
	 * Débouncé à 800 ms : ce formulaire contient des champs de saisie libre, et chaque enregistrement
	 * envoie le point d'intérêt et toutes ses valeurs de champs. Envoyer à chaque frappe n'aurait
	 * aucun sens.
	 */
	const autoSave = React.useMemo(() => debounce(async (values: any) => {
		clearTimeout(savedTimeout.current);
		setSaveState('saving');

		try {
			const previousLabel = model.label;

			await save(values);

			// Renommage : la liste et le texte des formes liées affichent le nom, ils suivent.
			if (model.label !== previousLabel) {
				leftSidebarStore.getListStore('places')?.reload();
				void refreshShapesOfPlace(model);
			}

			setSaveState('saved');
			savedTimeout.current = setTimeout(() => setSaveState('idle'), 2500);
		} catch (err) {
			setSaveState('error');
			message.exception(err);
		}
	}, 800), [model]);

	React.useEffect(() => () => autoSave.cancel(), [autoSave]);

	return (
		<Form
			updateOnChange
			onChange={tools => {
				if (!liveSave || !armedRef.current || fillingRef.current) return;

				// L'indicateur part dès la frappe, pas à l'envoi : l'enregistrement est différé de 800ms
				// (voir `autoSave`), et pendant tout ce temps l'écran ne montrait rien alors que la saisie
				// n'était pas encore partie. Il couvre donc l'attente autant que la requête.
				clearTimeout(savedTimeout.current);
				setSaveState('saving');

				autoSave(tools.getValues());
			}}
			// Aucun pied de formulaire dans la sidebar : tout part de lui-même.
			withButton={inSidebar ? false : undefined}
			onSuccess={onSuccess}
			onDidMount={async formTools => {
				formToolsRef.current = formTools;

				// Après l'appel que `Form` fait lui-même en sortant d'ici, donc hors de tout remplissage
				// initial : à partir de ce point, un changement vient forcément de l'utilisateur.
				setTimeout(() => {
					armedRef.current = true;
				});

				if (!created && model.label) fill({ label: model.label });

				if (created) {
					await fetchPlaceFields();

					if (placeImage && !inSidebar) {
						fill({ placeImage: { url: placeImage?.downloadPath } });
					}
				}
			}}
			fields={{
				// En modification dans la sidebar, le nom est posé par la fiche elle-même, en tête (voir
				// plus bas) : pas de second champ du même nom ici.
				...(liveSave ? {} : {
					label: {
						label: 'Titre',
						element: <Input autoFocus />,
						required: true,
						span: 24,
					},
				}),

				// En sidebar, la fiche dispose elle-même ces champs (voir plus bas) : l'image et le type
				// tiennent sur une ligne, les deux champs techniques passent sous un repli. En modale,
				// rien ne change — on y empile les champs les uns sous les autres, avec un bouton.
				...(inSidebar ? {} : {
					reference: {
						hidden: !created,
						label: 'Référence',
						element: <InputLock isLocked={created} />,
						span: 24,
						extra: (
							<View mt="sm">
								<Alert
									message="Identifiant du point d’intérêt permettant de le cibler et de le mettre en évidence sur la carte."
									type="info"
									showIcon
								/>
							</View>
						),
					},
					location: {
						hidden: !created,
						label: 'Emplacement',
						element: <Input />,
						span: 24,
					},
					placeImage: {
						hidden: !created,
						label: 'Image',
						element: <FileInput aspect="free" disableAspectCheck extensions={['jpg', 'png']} />,
						span: 24,
					},
					placeTypeUrn: {
						label: PlaceTypeModel.localize(),
						// Ni `formAddEnabled` ni `formEditEnabled` sur le sélecteur : `SelectCollection` les
						// rend dans une modale, et on en sort (voir `SelectPlaceType`).
						element: (
							<SelectPlaceType
								fetchOn="mount"
								projectUrn={projectUrn}
								onChange={async placeTypeUrn => setPlaceTypeUrn(placeTypeUrn as Urn)}
							/>
						),
						span: 24,
					},
				}),
			}}
			// Pas d'"Enregistrer et continuer" : on crée un point d'intérêt, on arrive sur sa fiche pour
			// la compléter — enchaîner sur une seconde création n'est pas le geste attendu ici.
			submitLabel="Enregistrer"
			onSubmit={values => save(values)}
		>
			{/* Un seul temps d'attente pour toute la fiche, plutôt qu'un écran qui se construit par
			    morceaux — voir `ready`. */}
			{created && inSidebar && !ready && (
				<View center py={64}>
					<Spin />
				</View>
			)}

			{created && inSidebar && ready && (
				<View gap="lg">
					<Field label="Nom">
						<FormItem name="label" rules={[{ required: true, whitespace: true, message: 'Le nom est obligatoire.' }]}>
							<Input placeholder={`Nom du ${PlaceModel.localize('default', 'l')}`} />
						</FormItem>
					</Field>

					{/* Le type, en tête de fiche. L'image l'accompagnait ici au départ ; elle est montée dans
					    l'en-tête avec le titre (`PlaceHeaderImage`) — ces deux-là identifient le point
					    d'intérêt, le type et ses champs le décrivent. */}
					{/* Tout le type tient dans cette tuile : un clic ouvre un menu où l'on choisit un autre
					    type, où l'on ouvre la fiche de celui-ci, ou où l'on en crée un. Un menu se referme
					    en cliquant ailleurs — d'où l'absence de bouton pour en sortir. */}
					<Field label={PlaceTypeModel.localize()}>
						<Observer render={() => (
							<Popover
								trigger="click"
								placement="bottomLeft"
								open={typeMenuOpen}
								onOpenChange={setTypeMenuOpen}
								styles={{ body: { padding: 0 } }}
								content={(
									<Observer render={() => (
										<View p="xs" gap={2} style={{ width: 240, maxHeight: 340, overflowY: 'auto' }}>
											{placeTypeCollection.map(placeType => {
												const current = placeType.urn === placeTypeUrn;

												return (
													<View
														key={placeType.id}
														row
														centerV
														gap="sm"
														px="sm"
														py="xs"
														br={8}
														pointer
														bg={current ? tokens.fill.subtle : undefined}
														hover={{ bg: tokens.surface.subtle }}
														onClick={() => current ? setTypeMenuOpen(false) : changeType(placeType)}
													>
														{/* Toujours présente, invisible quand ce n'est pas le type courant : sans
														    ça les libellés ne seraient pas alignés d'une ligne à l'autre. */}
														<CheckOutlined style={{ fontSize: 11, opacity: current ? 1 : 0, flexShrink: 0 }} />

														<View
															w={10}
															h={10}
															br="50%"
															style={{ background: placeType.color, flexShrink: 0 }}
														/>

														<View flex bold={current} size="small">{placeType.label}</View>
													</View>
												);
											})}

											{!!placeTypeCollection.length && <Divider style={{ margin: '4px 0' }} />}

											{!!placeTypeUrn && (
												<View
													row
													centerV
													gap="sm"
													px="sm"
													py="xs"
													br={8}
													pointer
													hover={{ bg: tokens.surface.subtle }}
													onClick={openTypeSheet}
												>
													<EditOutlined style={{ fontSize: 12 }} />
													<View flex size="small">
														Modifier « {model.placeType.label || '…'} »
													</View>
												</View>
											)}

											<View
												row
												centerV
												gap="sm"
												px="sm"
												py="xs"
												br={8}
												pointer
												color={tokens.color.primary}
												hover={{ bg: tokens.surface.subtle }}
												onClick={createAndEditPlaceType}
											>
												<PlusOutlined style={{ fontSize: 12 }} />
												<View flex size="small">Nouveau type</View>
											</View>
										</View>
									)} />
								)}
							>
								<View
									row
									centerV
									gap="sm"
									h={40}
									br={8}
									pointer
									bg={tokens.surface.elevated}
									hover={{ style: { borderColor: tokens.color.primary } }}
									style={{ padding: '0 11px', border: `1px solid ${tokens.border.default}`, transition: 'border-color 0.2s' }}
								>
									<View
										w={10}
										h={10}
										br="50%"
										style={{ background: model.placeType.color || tokens.border.default, flexShrink: 0 }}
									/>

									{/* Le type est une relation : il peut n'être pas encore résolu au premier rendu. Un
									    squelette plutôt qu'un nom vide, qui se lirait comme un type sans nom.

									    On teste le libellé lui-même, jamais `isLoaded` : c'est la lecture de la
									    propriété qui déclenche la résolution, la court-circuiter laisserait le
									    squelette tourner indéfiniment. */}
									{placeTypeUrn ? (
										<>
											<View flex size={14}>
												{model.placeType.label || <OneLineSkeleton width={90} />}
											</View>

											<View size={12} color={tokens.text.muted}>
												{placeTypeFieldCollection.length}
												{' '}
												{FieldModel.localize(placeTypeFieldCollection.length > 1 ? 'plural' : 'default', 'l')}
											</View>
										</>
									) : (
										<View flex size={14} color={tokens.text.muted}>
											Aucun {PlaceTypeModel.localize('default', 'l')}
										</View>
									)}

									{/* Un chevron vers le bas, pas vers la droite : ce clic ouvre un menu, il n'emmène
									    pas sur un autre écran. */}
									<DownOutlined style={{ color: tokens.text.muted, fontSize: 11 }} />
								</View>
							</Popover>
						)} />
					</Field>

					{/* Les champs du type : le contenu réel du point d'intérêt, donc juste sous son identité.
					    Le composant porte ses trois états — chargement, aucun champ, la liste — parce que
					    l'état vide remplace la section entière, séparateur compris. */}
					{!!placeTypeUrn && (
						<PlaceTypeFieldInputs
							key={placeTypeUrn}
							placeTypeFieldCollection={placeTypeFieldCollection}
							placeType={model.placeType}
							onConfigureType={openTypeSheet}
						/>
					)}

					{/* Même repli que dans la sidebar d'une forme (`AreaThemeSection`) : même nom, même boîte,
					    même chevron. Deux écrans qui rangent leurs réglages secondaires au même endroit
					    doivent le faire de la même façon. */}
					<View
						br={8}
						style={{ border: `1px solid ${tokens.border.default}`, background: tokens.surface.elevated, overflow: 'hidden' }}
					>
						<View
							row
							gap="xs"
							centerV
							bold
							size={13.5}
							p="sm"
							color={tokens.text.default}
							pointer
							onClick={() => setAdvancedOpen(open => !open)}
						>
							{advancedOpen ? <DownOutlined /> : <RightOutlined />}
							Options avancées
						</View>

						{advancedOpen && (
							<View px="sm" pb="sm" gap="sm">
								<FormItem
									name="reference"
									label="Référence"
									extra={(
										<View size="small" color={tokens.text.muted} mt="xs">
											Identifiant permettant de cibler ce point d'intérêt et de le mettre en
											évidence sur la carte.
										</View>
									)}
								>
									<InputLock isLocked placeholder="Ex. A-102" />
								</FormItem>

								<FormItem name="location" label="Emplacement">
									<Input placeholder="Ex. Niveau 1, près de l'entrée" />
								</FormItem>
							</View>
						)}
					</View>

					{/* L'aperçu ferme la fiche : après avoir rempli, on veut voir ce que ça donne. Il se
					    reconstruit à chaque frappe — `updateOnChange` redessine le formulaire entier — donc
					    il montre l'état courant, y compris ce qui n'est pas encore enregistré.

					    `Observer` pour la collection des champs du type, qui arrive après le premier rendu. */}
					<Observer render={() => {
						// `filledCount` n'est pas lu pour sa valeur : il est là pour que ce bloc soit redessiné
						// quand le formulaire vient d'être rempli par le code (voir sa déclaration).
						void filledCount;

						// `getFieldValue` et non `getValues` : ce dernier ne rend que les champs déjà montés, or cet
						// aperçu est dessiné dans le même rendu que les champs du type, avant qu'ils ne le soient.
						const valueOf = (name: string) => formToolsRef.current?.getFieldValue(name);

						const entry = buildPlacePanelEntry({
							label: valueOf('label') || model.label,
							location: valueOf('location') ?? model.location,
							reference: valueOf('reference') ?? model.reference,
							fields: placeTypeFieldCollection.map(placeTypeField => ({
								label: placeTypeField.field.label,
								reference: placeTypeField.field.reference,
								fieldType: placeTypeField.field.fieldType.reference,
								value: valueOf(`placeTypeField.${placeTypeField.id}`),
							})),
						});

						return (
							<PlacePanelPreview
								entry={entry}
								image={imageOf(model, PLACE_IMAGE)?.downloadPath}
							/>
						);
					}} />

				</View>
			)}

			{/* En modale, les champs du type restent en bas : cet écran-là empile ses champs et se valide
			    d'un bouton, il n'a pas de mise en page à lui. Pas d'`onConfigureType` — la fiche d'un type
			    s'ouvre dans la sidebar, qui n'existe pas ici. */}
			{created && !inSidebar && !!placeTypeUrn && (
				<PlaceTypeFieldInputs
					key={placeTypeUrn}
					placeTypeFieldCollection={placeTypeFieldCollection}
					placeType={model.placeType}
				/>
			)}
		</Form>
	);
}